import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["partner_user", "partner_manager", "partner_success_manager", "super_user"];

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Welcome123!", role: "partner_user", partner_id: "", partner_type: "" });

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/partners").catch(() => ({ data: [] }))]);
    setUsers(u.data || []); setPartners(p.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/users", { ...form, partner_id: form.partner_id || null });
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "Welcome123!", role: "partner_user", partner_id: "", partner_type: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setRole = async (uid, role) => { await api.patch(`/users/${uid}`, { role }); load(); };
  const setPartner = async (uid, partner_id) => { await api.patch(`/users/${uid}`, { partner_id }); load(); };
  const remove = async (uid) => { if (confirm("Delete user?")) { await api.delete(`/users/${uid}`); load(); } };

  const canEditRole = user?.role === "super_user";

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-1">Users</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="new-user-btn"><Plus size={16} className="mr-1" /> New user</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-md">
            <DialogHeader><DialogTitle>Create user</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-sm mt-1" data-testid="user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="rounded-sm mt-1" data-testid="user-email" /></div>
              <div><Label>Temp password</Label><Input value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="rounded-sm mt-1" /></div>
              {canEditRole && (
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                    <SelectTrigger className="rounded-sm mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {partners.length > 0 && (
                <div><Label>Assign partner</Label>
                  <Select value={form.partner_id} onValueChange={(v) => setForm({...form, partner_id: v})}>
                    <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Optional" /></SelectTrigger>
                    <SelectContent>{partners.map((p) => <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter><Button onClick={submit} className="rounded-sm bg-[var(--uc-primary)] hover:bg-[var(--uc-primary-hover)] text-white" data-testid="user-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-border overflow-hidden">
        <table className="w-full text-sm dense">
          <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Partner</th><th>Auth</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-t border-border" data-testid={`user-row-${u.user_id}`}>
                <td className="font-medium">{u.name}</td>
                <td className="text-muted-foreground">{u.email}</td>
                <td>
                  {canEditRole ? (
                    <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs">{u.role}</span>}
                </td>
                <td>
                  {canEditRole && partners.length > 0 ? (
                    <Select value={u.partner_id || ""} onValueChange={(v) => setPartner(u.user_id, v)}>
                      <SelectTrigger className="h-8 w-44 rounded-sm"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{partners.map((p) => <SelectItem key={p.partner_id} value={p.partner_id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-xs text-muted-foreground">{partners.find(p => p.partner_id === u.partner_id)?.name || "—"}</span>}
                </td>
                <td><span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{u.auth_provider}</span></td>
                <td className="text-right">{canEditRole && u.user_id !== user.user_id &&
                  <button className="p-2 text-muted-foreground hover:text-destructive" onClick={() => remove(u.user_id)} data-testid={`user-del-${u.user_id}`}><Trash size={14} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
